import * as api from "@/api";
import LocalWallpaper from "@/classes/local-wallpaper";
import QueryDataManager from "@/classes/query-data-manager";
import PopupShell from "@/components/popup-shell";
import styles from "@/components/wallpaper-popup.module.css";
import WallpaperThumbnail from "@/components/wallpaper-thumbnail";
import presetWallpapers from "@/data/preset-wallpapers";
import { globals } from "@/globals";
import useBingWallpaper from "@/hooks/use-bing-wallpaper";
import * as signals from "@/signals";
import { DialogIcon } from "@/types";
import {
	closePopup,
	copyText,
	downloadExtension,
	getRedirectLink,
	showDialog,
	showPrompt,
	updateSettings,
} from "@/utils";
import {
	faDownload,
	faFolderOpen,
	faHeart,
	faInfoCircle,
	faLink,
	faPalette,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { t } from "i18next";
import { ChangeEvent, JSX, useEffect, useRef, useState } from "react";

interface WallpaperPopupProps {
	isShown: boolean;
}

function WallpaperPopup({ isShown }: WallpaperPopupProps): JSX.Element {
	const queryClient = useQueryClient();

	const fileInputRef = useRef<HTMLInputElement>(null);

	const [isBounceAnimationBeingPlayed, setIsBounceAnimationBeingPlayed] =
		useState<boolean>(false);
	const [customWallpaperUrl, setCustomWallpaperUrl] = useState<string | null>(
		null,
	);

	const {
		bingWallpaperCopyright,
		bingWallpaperDescription,
		bingWallpaperResponse,
		bingWallpaperTitle,
		bingWallpaperUrl,
		bingWallpaperUrlUhd,
	} = useBingWallpaper();

	const bingWallpaperFullDescription =
		bingWallpaperDescription + " (" + bingWallpaperCopyright + ")";

	const closeWallpaperPopup = (): void => {
		closePopup("wallpaper");
	};

	const copyBingWallpaperFullDescription = (): void => {
		if (!bingWallpaperFullDescription) {
			return;
		}
		void copyText(bingWallpaperFullDescription);
	};

	const handleFileInputChange = async (
		event: ChangeEvent<HTMLInputElement>,
	): Promise<void> => {
		const { files } = event.target;
		if (!files || files.length === 0) {
			return;
		}
		const localWallpaper = LocalWallpaper.getInstance();
		await localWallpaper.saveWallpaper(files);
		if (localWallpaper.isVideo) {
			await localWallpaper.createVideoThumbnail();
			setCustomWallpaperUrl(localWallpaper.videoThumbnailUrl);
		} else {
			setCustomWallpaperUrl(localWallpaper.wallpaperUrl);
		}
	};

	const likeBingWallpaper = async (): Promise<void> => {
		setIsBounceAnimationBeingPlayed(true);
		signals.isLoadingShown.value = true;
		try {
			await new QueryDataManager(queryClient).likeBingWallpaper({
				bingWallpaperResponse,
			});
		} catch (error) {
			void api.handleApiError(error);
		} finally {
			signals.isLoadingShown.value = false;
		}
	};

	const selectLocalFile = (): void => {
		fileInputRef.current?.click();
	};

	const setBingWallpaper = (): void => {
		signals.wallpaper.value = {
			isVideo: false,
			url: bingWallpaperUrl,
		};
		void updateSettings({
			currentWallpaper: "Bing",
		});
	};

	const setCustomWallpaper = async (): Promise<void> => {
		if (!customWallpaperUrl) {
			return;
		}
		const localWallpaper = LocalWallpaper.getInstance();
		const wallpaperFile = await localWallpaper.getWallpaper();
		if (!wallpaperFile) {
			return;
		}
		localWallpaper.setWallpaper();
		await updateSettings({
			currentWallpaper: "Custom",
		});
	};

	const setOnlineImageUrl = async (): Promise<void> => {
		const url = await showPrompt(t("enterOnlineImageUrl"), {
			type: "url",
		});
		if (!url) {
			return;
		}
		if (!url.startsWith("https://")) {
			void showDialog(t("urlMustStartWithHttps"), {
				title: t("error"),
			});
			return;
		}
		setCustomWallpaperUrl(url);
		signals.wallpaper.value = {
			isVideo: false,
			url: url,
		};
		await updateSettings({
			currentWallpaper: url,
		});
		const localWallpaper = LocalWallpaper.getInstance();
		void localWallpaper.deleteWallpaper();
	};

	const liveWallpaperElements = presetWallpapers.live.map((item, index) => {
		const handleClick = async (): Promise<void> => {
			if (globals.isExtension) {
				if (globals.extensionInfo) {
					signals.wallpaper.value = {
						isVideo: true,
						url:
							signals.liveWallpaperPrefix.value +
							item.id +
							".mp4",
					};
					void updateSettings({
						currentWallpaper: item.id,
					});
				} else {
					void showDialog(
						t("noSiteAccessDescription", {
							minVersion: "1.3.9",
						}),
						{
							icon: DialogIcon.ERROR,
							title: t("noSiteAccess"),
						},
					);
				}
			} else {
				await showDialog(t("liveWallpaperNeedsExtension"), {
					showCancel: true,
				});
				downloadExtension();
			}
		};

		return (
			<WallpaperThumbnail
				description={item.description}
				isSelected={
					signals.generalSettings.value.currentWallpaper === item.id
				}
				key={index}
				onClick={handleClick}
				thumbnail={item.thumbnail}
			/>
		);
	});

	const wallpaperElements = presetWallpapers.static.map((item, index) => {
		const handleClick = (): void => {
			signals.wallpaper.value = {
				fallbackUrl: item.fallbackUrl,
				isVideo: false,
				url: item.url,
			};
			void updateSettings({
				currentWallpaper: item.id,
			});
		};

		return (
			<WallpaperThumbnail
				description={item.description}
				isSelected={
					signals.generalSettings.value.currentWallpaper === item.id
				}
				key={index}
				onClick={handleClick}
				thumbnail={item.thumbnail}
			/>
		);
	});

	useEffect(() => {
		const localWallpaper = LocalWallpaper.getInstance();
		void localWallpaper
			.getWallpaperUrl()
			.then((url) => {
				setCustomWallpaperUrl(url);
			})
			.catch(console.error);
	}, []);

	return (
		<PopupShell
			isPureWhite={true}
			isShown={isShown}
			onClose={closeWallpaperPopup}
			title={t("wallpaperPreferences")}
		>
			<div className={styles["group-title"]}>{t("custom")}</div>
			<div className={styles["wallpaper-group"]}>
				<WallpaperThumbnail
					description="自定义壁纸"
					isSelected={
						signals.wallpaper.value.url?.startsWith("blob:") ||
						signals.wallpaper.value.url === customWallpaperUrl
					}
					onClick={setCustomWallpaper}
					thumbnail={customWallpaperUrl || ""}
				/>
				<div className={styles["side-content"]}>
					<h1>{t("setYourFavoritePictureVideoAsWallpaper")}</h1>
					<div className={styles["side-content-description"]}>
						{t("setCustomWallpaperDescription")}
					</div>
					<div className={styles["custom-wallpaper-actions"]}>
						<button
							aria-haspopup="dialog"
							className={styles["action-button"]}
							onClick={selectLocalFile}
							type="button"
						>
							<FontAwesomeIcon icon={faFolderOpen} />
							{t("localImageVideo")}
						</button>
						<button
							aria-haspopup="dialog"
							className={styles["action-button"]}
							onClick={() => void setOnlineImageUrl()}
							type="button"
						>
							<FontAwesomeIcon icon={faLink} />
							{t("onlineImageUrl")}
						</button>
						<a
							href={getRedirectLink("gaoding")}
							target="_blank"
							rel="noreferrer"
						>
							<FontAwesomeIcon icon={faPalette} />
							{t("aiImageGenerator")}
							<i>&#xe77c;</i>
						</a>
					</div>
				</div>
			</div>
			<div className={styles["group-title"]}>{t("default")}</div>
			<div className={styles["wallpaper-group"]}>{wallpaperElements}</div>
			<div className={styles["group-title"]}>{t("today")}</div>
			<div className={styles["wallpaper-group"]}>
				<WallpaperThumbnail
					description={bingWallpaperFullDescription}
					isSelected={
						signals.generalSettings.value.currentWallpaper ===
						"Bing"
					}
					onClick={setBingWallpaper}
					thumbnail={bingWallpaperUrl}
				/>
				<div className={styles["side-content"]}>
					<h1>{bingWallpaperTitle}</h1>
					<div
						className={styles["side-content-description"]}
						onContextMenu={copyBingWallpaperFullDescription}
						title={t("rightClickToCopy")}
					>
						{bingWallpaperFullDescription}
					</div>
					<div className={styles["daily-wallpaper-actions"]}>
						<button
							className={clsx(
								styles["action-button"],
								isBounceAnimationBeingPlayed && "bounce",
							)}
							onClick={() => void likeBingWallpaper()}
							type="button"
						>
							<FontAwesomeIcon icon={faHeart} />
							{bingWallpaperResponse?.likeCount}
						</button>
						<a
							className={styles["action-button"]}
							href={bingWallpaperUrlUhd}
							rel="noreferrer"
							target="_blank"
						>
							<FontAwesomeIcon icon={faDownload} />
							{t("downloadImage")}
						</a>
						<div>{t("imageSource") + t("bing")}</div>
					</div>
				</div>
			</div>
			<div className={styles["group-title"]}>{t("live")}</div>
			{globals.isEdge && globals.extensionInfo && (
				<div className={styles["dynamic-wallpaper-tip"]}>
					<FontAwesomeIcon icon={faInfoCircle} />
					{t("liveWallpaperTip")}
				</div>
			)}
			<div className={styles["wallpaper-group"]}>
				{liveWallpaperElements}
			</div>
			<input
				accept={globals.isMobile ? "image/*" : "image/*, video/*"}
				hidden={true}
				multiple={true}
				onChange={(event) => void handleFileInputChange(event)}
				ref={fileInputRef}
				type="file"
			/>
		</PopupShell>
	);
}

export default WallpaperPopup;
